// ---------------------------------------------------------------------------
// Ordenação das sugestões do autocomplete de funcionário (2026-09-11).
//
// Duas chaves, nesta ordem:
//
//   1. SITUAÇÃO — `DESLIGADO` sempre por último. É chave EXTERNA: um desligado
//      com nome idêntico à busca ainda cai para baixo de qualquer ativo. Os
//      demais status não são ordenados entre si (férias/afastado/experiência
//      são gente trabalhando; a hierarquia de correspondência basta).
//
//   2. PROXIMIDADE DA CORRESPONDÊNCIA — do mais próximo ao mais distante:
//        1. nome inteiro igual à busca            → "MARIA"
//        2. o nome COMEÇA com a busca             → "MARIA ADELINA…"
//        3. alguma palavra é IGUAL à busca        → "GERCYKA MARIA LEONIDAS"
//        4. alguma palavra COMEÇA com a busca     → "LETICIA MARIANO…"
//        5. aparece no meio de uma palavra        → "IRAMARIA DA SILVA"
//      Empate desempata pela posição da primeira ocorrência (mais à esquerda
//      primeiro); o resto preserva a ordem que o backend mandou (o `sort` do
//      JS é estável), que hoje é alfabética.
//
// ⚠️ Isto REORDENA a página que o backend devolveu — não é ranking global. Com
// `limit` de sugestões, quem o backend cortou continua fora; a ordenação só
// arruma o que chegou. Ranking de verdade seria backend-first.
//
// Invariante: a função nunca descarta nem duplica item. Nome que não casa com
// a busca (o backend pode ter casado por outro campo, ou o acento escapou da
// normalização) vai para o fim do seu bloco de status, nunca some.
// ---------------------------------------------------------------------------

// Compara sem acento e sem caixa: buscar "marcia" precisa casar com "MÁRCIA",
// senão a busca sem acento cairia no nível "não casou" e o funcionário
// desabaria para o fim da lista.
//
// O NFD separa a letra da marca de acento; as marcas vivem no bloco Unicode
// Combining Diacritical Marks (U+0300..U+036F) e são descartadas aqui POR
// CÓDIGO DE CARACTERE, de propósito: a regex equivalente exige as marcas
// combinantes literais no fonte, que aparecem como lixo em qualquer editor e
// convidam a um "conserto" errado.
const ACCENT_MARK_FIRST = 0x300;
const ACCENT_MARK_LAST = 0x36f;

function stripAccents(value) {
   return Array.from(value.normalize('NFD'))
      .filter((char) => {
         const code = char.codePointAt(0);
         return code < ACCENT_MARK_FIRST || code > ACCENT_MARK_LAST;
      })
      .join('');
}

function normalize(value) {
   return stripAccents(String(value ?? ''))
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');
}

const NO_MATCH = 6;

function proximityTier(fullName, query) {
   const name = normalize(fullName);
   const term = normalize(query);
   if (!term || !name) return NO_MATCH;

   if (name === term) return 1;
   if (name.startsWith(term)) return 2;

   const words = name.split(' ');
   if (words.includes(term)) return 3;
   if (words.some((word) => word.startsWith(term))) return 4;
   if (name.includes(term)) return 5;

   return NO_MATCH;
}

function isDismissed(employee) {
   return normalize(employee?.employment_status) === 'desligado' ? 1 : 0;
}

export default function rankSuggestions(employees, query) {
   const list = Array.isArray(employees) ? employees : [];

   // Índices pré-calculados: o comparador roda O(n log n) vezes e normalizar
   // nome dentro dele refaria o mesmo trabalho a cada comparação.
   const scored = list.map((employee, index) => ({
      employee,
      index,
      dismissed: isDismissed(employee),
      tier: proximityTier(employee?.full_name, query),
      at: normalize(employee?.full_name).indexOf(normalize(query)),
   }));

   scored.sort((a, b) => {
      if (a.dismissed !== b.dismissed) return a.dismissed - b.dismissed;
      if (a.tier !== b.tier) return a.tier - b.tier;
      if (a.at !== b.at) return a.at - b.at;
      return a.index - b.index; // preserva a ordem do backend no empate
   });

   return scored.map((entry) => entry.employee);
}
